import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatEther, parseEther, parseGwei, type Address } from "viem";
import {
  useAccount,
  usePublicClient,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";

import { factoryAbi } from "../contracts/factoryAbi";
import { factoryAddress } from "../contracts/factoryAddress";

type PortfolioItem = {
  marketId: bigint;
  question: string;
  resolutionSource: string;
  deadline: bigint;
  creator: string;
  resolved: boolean;
  outcome: boolean;
  yesPool: bigint;
  noPool: bigint;
  totalYesShares: bigint;
  totalNoShares: bigint;
  virtualLiquidity: bigint;
  resolutionEvidenceUrl: string;
  resolutionNotes: string;
  resolver: string;
  resolvedAt: bigint;
  yesPriceBps: bigint;
  noPriceBps: bigint;
  yesShares: bigint;
  noShares: bigint;
  yesCost: bigint;
  noCost: bigint;
  alreadyClaimed: boolean;
  statusLabel: string;
  estimatedIfYesWins: bigint;
  estimatedIfNoWins: bigint;
  claimablePayout: bigint;
  currentYesValue: bigint;
  currentNoValue: bigint;
  currentPositionValue: bigint;
  avgYesEntryBps: bigint;
  avgNoEntryBps: bigint;
  pnlYes: bigint;
  pnlNo: bigint;
  pnlTotal: bigint;
};

type SellDraft = {
  marketId: string;
  side: "YES" | "NO";
  amount: string;
};

const AMOY_MAX_PRIORITY_FEE_PER_GAS = parseGwei("30");
const AMOY_MAX_FEE_PER_GAS = parseGwei("60");

function formatPol(value: bigint, decimals = 4) {
  try {
    const num = Number(formatEther(value));
    if (!Number.isFinite(num)) return "0";
    return num.toFixed(decimals);
  } catch {
    return "0";
  }
}

function formatSignedPol(value: bigint, decimals = 4) {
  const sign = value < 0n ? "-" : "+";
  const absValue = value < 0n ? -value : value;
  return `${sign}${formatPol(absValue, decimals)}`;
}

function formatDate(timestamp: bigint) {
  const ms = Number(timestamp) * 1000;
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  return new Date(ms).toLocaleString();
}

function formatPercentFromBps(value: bigint) {
  return `${(Number(value) / 100).toFixed(1)}%`;
}

function shortAddress(value: string, left = 6, right = 4) {
  if (!value) return "—";
  if (value.length <= left + right) return value;
  return `${value.slice(0, left)}...${value.slice(-right)}`;
}

function safeBigInt(value: unknown): bigint {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") return BigInt(value);
  if (typeof value === "string") {
    try {
      return BigInt(value);
    } catch {
      return 0n;
    }
  }
  return 0n;
}

function safeBoolean(value: unknown): boolean {
  return Boolean(value);
}

function safeString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeMarket(raw: unknown) {
  if (Array.isArray(raw)) {
    return {
      id: safeBigInt(raw[0]),
      question: safeString(raw[1]),
      resolutionSource: safeString(raw[2]),
      deadline: safeBigInt(raw[3]),
      creator: safeString(raw[4]),
      resolved: safeBoolean(raw[5]),
      outcome: safeBoolean(raw[6]),
      yesPool: safeBigInt(raw[7]),
      noPool: safeBigInt(raw[8]),
      totalYesShares: safeBigInt(raw[9]),
      totalNoShares: safeBigInt(raw[10]),
      virtualLiquidity: safeBigInt(raw[11]),
      resolutionEvidenceUrl: safeString(raw[12]),
      resolutionNotes: safeString(raw[13]),
      resolver: safeString(raw[14]),
      resolvedAt: safeBigInt(raw[15]),
    };
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return {
      id: safeBigInt(obj.id),
      question: safeString(obj.question),
      resolutionSource: safeString(obj.resolutionSource),
      deadline: safeBigInt(obj.deadline),
      creator: safeString(obj.creator),
      resolved: safeBoolean(obj.resolved),
      outcome: safeBoolean(obj.outcome),
      yesPool: safeBigInt(obj.yesPool),
      noPool: safeBigInt(obj.noPool),
      totalYesShares: safeBigInt(obj.totalYesShares),
      totalNoShares: safeBigInt(obj.totalNoShares),
      virtualLiquidity: safeBigInt(obj.virtualLiquidity),
      resolutionEvidenceUrl: safeString(obj.resolutionEvidenceUrl),
      resolutionNotes: safeString(obj.resolutionNotes),
      resolver: safeString(obj.resolver),
      resolvedAt: safeBigInt(obj.resolvedAt),
    };
  }

  return {
    id: 0n,
    question: "",
    resolutionSource: "",
    deadline: 0n,
    creator: "",
    resolved: false,
    outcome: false,
    yesPool: 0n,
    noPool: 0n,
    totalYesShares: 0n,
    totalNoShares: 0n,
    virtualLiquidity: 0n,
    resolutionEvidenceUrl: "",
    resolutionNotes: "",
    resolver: "",
    resolvedAt: 0n,
  };
}

function normalizePrices(raw: unknown) {
  if (Array.isArray(raw)) {
    return {
      yesPriceBps: safeBigInt(raw[0]),
      noPriceBps: safeBigInt(raw[1]),
    };
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return {
      yesPriceBps: safeBigInt(obj.yesPriceBps ?? obj.yesPrice),
      noPriceBps: safeBigInt(obj.noPriceBps ?? obj.noPrice),
    };
  }

  return {
    yesPriceBps: 0n,
    noPriceBps: 0n,
  };
}

function normalizePosition(raw: unknown) {
  if (Array.isArray(raw)) {
    return {
      yesShares: safeBigInt(raw[0]),
      noShares: safeBigInt(raw[1]),
      alreadyClaimed: safeBoolean(raw[2]),
    };
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return {
      yesShares: safeBigInt(obj.yesAmount ?? obj.yesShares),
      noShares: safeBigInt(obj.noAmount ?? obj.noShares),
      alreadyClaimed: safeBoolean(obj.alreadyClaimed),
    };
  }

  return {
    yesShares: 0n,
    noShares: 0n,
    alreadyClaimed: false,
  };
}

function normalizeCostBasis(raw: unknown) {
  if (Array.isArray(raw)) {
    return {
      yesCost: safeBigInt(raw[0]),
      noCost: safeBigInt(raw[1]),
    };
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return {
      yesCost: safeBigInt(obj.yesCost),
      noCost: safeBigInt(obj.noCost),
    };
  }

  return {
    yesCost: 0n,
    noCost: 0n,
  };
}

function calculateResolvedPayout(
  userWinnerShares: bigint,
  totalWinnerShares: bigint,
  totalPool: bigint
) {
  if (userWinnerShares <= 0n || totalWinnerShares <= 0n || totalPool <= 0n) {
    return 0n;
  }
  return (userWinnerShares * totalPool) / totalWinnerShares;
}

function calculateCurrentValue(shares: bigint, priceBps: bigint) {
  if (shares <= 0n || priceBps <= 0n) return 0n;
  return (shares * priceBps) / 10000n;
}

function calculateAverageEntryBps(cost: bigint, shares: bigint) {
  if (cost <= 0n || shares <= 0n) return 0n;
  return (cost * 10000n) / shares;
}

function getStatusLabel(
  item: Pick<PortfolioItem, "resolved" | "alreadyClaimed" | "claimablePayout">
) {
  if (item.resolved && item.alreadyClaimed) return "Claimed";
  if (item.resolved && item.claimablePayout > 0n) return "Winning";
  if (item.resolved) return "Lost";
  return "Active";
}

export default function PortfolioPage() {
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();

  const [positions, setPositions] = useState<PortfolioItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [claimingMarketId, setClaimingMarketId] = useState<string | null>(null);
  const [sellDraft, setSellDraft] = useState<SellDraft | null>(null);

  const contractAddress = factoryAddress as Address;

  const {
    writeContract,
    data: txHash,
    isPending: isWritePending,
    error: writeError,
    reset,
  } = useWriteContract();

  const { isLoading: isTxConfirming, isSuccess: isTxConfirmed } =
    useWaitForTransactionReceipt({
      hash: txHash,
      query: { enabled: !!txHash },
    });

  const totals = useMemo(() => {
    const totalClaimable = positions.reduce(
      (sum, item) => sum + item.claimablePayout,
      0n
    );
    const totalCurrentValue = positions.reduce(
      (sum, item) => sum + item.currentPositionValue,
      0n
    );
    const totalCost = positions.reduce(
      (sum, item) => sum + item.yesCost + item.noCost,
      0n
    );
    const totalPnl = positions.reduce((sum, item) => sum + item.pnlTotal, 0n);

    return {
      totalClaimable,
      totalCurrentValue,
      totalCost,
      totalPnl,
      marketsCount: positions.length,
    };
  }, [positions]);

  useEffect(() => {
    if (!publicClient || !address || !isConnected) {
      setPositions([]);
      return;
    }

    let cancelled = false;

    async function loadPortfolio() {
      try {
        setIsLoading(true);
        setErrorMessage("");

        const marketIdsRaw = await publicClient.readContract({
          address: contractAddress,
          abi: factoryAbi,
          functionName: "getActiveMarketIds",
        });

        const marketIds = Array.isArray(marketIdsRaw)
          ? marketIdsRaw.map((value) => safeBigInt(value))
          : [];

        const loadedPositions: PortfolioItem[] = [];

        for (const marketId of marketIds) {
          const [marketRaw, pricesRaw, userPositionRaw, costBasisRaw] =
            await Promise.all([
              publicClient.readContract({
                address: contractAddress,
                abi: factoryAbi,
                functionName: "getMarket",
                args: [marketId],
              }),
              publicClient.readContract({
                address: contractAddress,
                abi: factoryAbi,
                functionName: "getPrices",
                args: [marketId],
              }),
              publicClient.readContract({
                address: contractAddress,
                abi: factoryAbi,
                functionName: "getUserPosition",
                args: [marketId, address],
              }),
              publicClient.readContract({
                address: contractAddress,
                abi: factoryAbi,
                functionName: "getUserCostBasis",
                args: [marketId, address],
              }),
            ]);

          const market = normalizeMarket(marketRaw);
          const prices = normalizePrices(pricesRaw);
          const userPosition = normalizePosition(userPositionRaw);
          const costBasis = normalizeCostBasis(costBasisRaw);

          if (userPosition.yesShares === 0n && userPosition.noShares === 0n) {
            continue;
          }

          const totalPool = market.yesPool + market.noPool;

          const estimatedIfYesWins = calculateResolvedPayout(
            userPosition.yesShares,
            market.totalYesShares,
            totalPool
          );

          const estimatedIfNoWins = calculateResolvedPayout(
            userPosition.noShares,
            market.totalNoShares,
            totalPool
          );

          const claimablePayout =
            market.resolved && !userPosition.alreadyClaimed
              ? market.outcome
                ? estimatedIfYesWins
                : estimatedIfNoWins
              : 0n;

          const currentYesValue = calculateCurrentValue(
            userPosition.yesShares,
            prices.yesPriceBps
          );

          const currentNoValue = calculateCurrentValue(
            userPosition.noShares,
            prices.noPriceBps
          );

          const currentPositionValue = currentYesValue + currentNoValue;

          const avgYesEntryBps = calculateAverageEntryBps(
            costBasis.yesCost,
            userPosition.yesShares
          );

          const avgNoEntryBps = calculateAverageEntryBps(
            costBasis.noCost,
            userPosition.noShares
          );

          const pnlYes = currentYesValue - costBasis.yesCost;
          const pnlNo = currentNoValue - costBasis.noCost;
          const pnlTotal = pnlYes + pnlNo;

          const draft: PortfolioItem = {
            marketId,
            question: market.question,
            resolutionSource: market.resolutionSource,
            deadline: market.deadline,
            creator: market.creator,
            resolved: market.resolved,
            outcome: market.outcome,
            yesPool: market.yesPool,
            noPool: market.noPool,
            totalYesShares: market.totalYesShares,
            totalNoShares: market.totalNoShares,
            virtualLiquidity: market.virtualLiquidity,
            resolutionEvidenceUrl: market.resolutionEvidenceUrl,
            resolutionNotes: market.resolutionNotes,
            resolver: market.resolver,
            resolvedAt: market.resolvedAt,
            yesPriceBps: prices.yesPriceBps,
            noPriceBps: prices.noPriceBps,
            yesShares: userPosition.yesShares,
            noShares: userPosition.noShares,
            yesCost: costBasis.yesCost,
            noCost: costBasis.noCost,
            alreadyClaimed: userPosition.alreadyClaimed,
            statusLabel: "Active",
            estimatedIfYesWins,
            estimatedIfNoWins,
            claimablePayout,
            currentYesValue,
            currentNoValue,
            currentPositionValue,
            avgYesEntryBps,
            avgNoEntryBps,
            pnlYes,
            pnlNo,
            pnlTotal,
          };

          draft.statusLabel = getStatusLabel(draft);
          loadedPositions.push(draft);
        }

        loadedPositions.sort((a, b) => Number(a.marketId - b.marketId));

        if (!cancelled) {
          setPositions(loadedPositions);
        }
      } catch (error) {
        console.error("Failed to load portfolio:", error);
        if (!cancelled) {
          setErrorMessage("Failed to load portfolio.");
          setPositions([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadPortfolio();

    return () => {
      cancelled = true;
    };
  }, [publicClient, address, isConnected, contractAddress, refreshNonce]);

  useEffect(() => {
    if (!isTxConfirmed) return;
    setClaimingMarketId(null);
    setSellDraft(null);
    setRefreshNonce((prev) => prev + 1);
  }, [isTxConfirmed]);

  function handleClaim(marketId: bigint) {
    setClaimingMarketId(marketId.toString());

    writeContract({
      address: contractAddress,
      abi: factoryAbi,
      functionName: "claimWinnings",
      args: [marketId],
      maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
      maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
    } as any);
  }

  function openSellDraft(marketId: bigint, side: "YES" | "NO") {
    setSellDraft({
      marketId: marketId.toString(),
      side,
      amount: "",
    });
    reset();
  }

  function closeSellDraft() {
    setSellDraft(null);
    reset();
  }

  function setSellAmount(amount: string) {
    setSellDraft((prev) => (prev ? { ...prev, amount } : prev));
  }

  function handleSell(marketId: bigint, side: "YES" | "NO") {
    if (!sellDraft || sellDraft.marketId !== marketId.toString()) return;

    let amountWei: bigint;
    try {
      amountWei = parseEther(sellDraft.amount || "0");
    } catch {
      return;
    }

    if (amountWei <= 0n) return;

    writeContract({
      address: contractAddress,
      abi: factoryAbi,
      functionName: "sellShares",
      args: [marketId, side === "YES", amountWei],
      maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
      maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
    } as any);
  }

  if (!isConnected || !address) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          <h1 className="text-2xl font-semibold">Portfolio</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Connect your wallet to view your positions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Portfolio</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your Orion positions, average entry, current value, real PnL, claimable winnings, and sell actions.
        </p>
      </div>

      <div className="mb-8 grid gap-4 md:grid-cols-5">
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Markets</div>
          <div className="mt-2 text-2xl font-semibold">{totals.marketsCount}</div>
        </div>

        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Total Cost</div>
          <div className="mt-2 text-2xl font-semibold">
            {formatPol(totals.totalCost)} POL
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Current Value</div>
          <div className="mt-2 text-2xl font-semibold">
            {formatPol(totals.totalCurrentValue)} POL
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Total PnL</div>
          <div className="mt-2 text-2xl font-semibold">
            {formatSignedPol(totals.totalPnl)} POL
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Claimable Now</div>
          <div className="mt-2 text-2xl font-semibold">
            {formatPol(totals.totalClaimable)} POL
          </div>
        </div>
      </div>

      {(isWritePending || isTxConfirming || writeError || isTxConfirmed) && (
        <div className="mb-6 rounded-2xl border bg-card p-4 text-sm shadow-sm">
          {isWritePending && (
            <div className="text-muted-foreground">
              Please confirm the transaction in your wallet.
            </div>
          )}
          {isTxConfirming && (
            <div className="text-muted-foreground">
              Transaction submitted. Waiting for blockchain confirmation...
            </div>
          )}
          {isTxConfirmed && (
            <div className="text-emerald-600">
              Transaction successful. Portfolio refreshed.
            </div>
          )}
          {writeError && (
            <div className="text-red-500">
              {writeError.message || "Transaction failed."}
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div className="rounded-2xl border bg-card p-6 text-sm text-muted-foreground shadow-sm">
          Loading portfolio...
        </div>
      )}

      {!isLoading && errorMessage && (
        <div className="rounded-2xl border bg-card p-6 text-sm text-red-500 shadow-sm">
          {errorMessage}
        </div>
      )}

      {!isLoading && !errorMessage && positions.length === 0 && (
        <div className="rounded-2xl border bg-card p-6 text-sm text-muted-foreground shadow-sm">
          No positions found yet.
        </div>
      )}

      {!isLoading && !errorMessage && positions.length > 0 && (
        <div className="space-y-4">
          {positions.map((item) => {
            const totalPool = item.yesPool + item.noPool;

            const canClaim =
              item.resolved &&
              item.claimablePayout > 0n &&
              !item.alreadyClaimed &&
              !isWritePending &&
              !isTxConfirming;

            const isSellingThisMarket =
              sellDraft?.marketId === item.marketId.toString();

            let selectedSellShares = 0n;
            let selectedAvailableShares = 0n;

            if (sellDraft?.marketId === item.marketId.toString()) {
              try {
                selectedSellShares = parseEther(sellDraft.amount || "0");
              } catch {
                selectedSellShares = 0n;
              }

              selectedAvailableShares =
                sellDraft.side === "YES" ? item.yesShares : item.noShares;
            }

            const sellTooHigh =
              isSellingThisMarket &&
              selectedSellShares > selectedAvailableShares;

            const canSell =
              !item.resolved &&
              isSellingThisMarket &&
              selectedSellShares > 0n &&
              !sellTooHigh &&
              !isWritePending &&
              !isTxConfirming;

            return (
              <div
                key={item.marketId.toString()}
                className="rounded-2xl border bg-card p-6 shadow-sm"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="max-w-3xl">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      Market #{item.marketId.toString()}
                    </div>

                    <h2 className="mt-2 text-2xl font-semibold leading-tight">
                      {item.question}
                    </h2>

                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full border px-3 py-1 text-muted-foreground">
                        Deadline: {formatDate(item.deadline)}
                      </span>

                      <span className="rounded-full border px-3 py-1 text-muted-foreground">
                        Source: {item.resolutionSource || "—"}
                      </span>

                      <span
                        className={`rounded-full px-3 py-1 ${
                          item.statusLabel === "Winning"
                            ? "border border-emerald-500/20 bg-emerald-500/10 text-emerald-600"
                            : item.statusLabel === "Claimed"
                            ? "border border-slate-500/20 bg-slate-500/10 text-slate-600"
                            : item.statusLabel === "Lost"
                            ? "border border-rose-500/20 bg-rose-500/10 text-rose-600"
                            : "border border-amber-500/20 bg-amber-500/10 text-amber-600"
                        }`}
                      >
                        {item.statusLabel}
                      </span>

                      {item.resolved && (
                        <span className="rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1 text-sky-600">
                          Outcome: {item.outcome ? "YES" : "NO"}
                        </span>
                      )}

                      {item.claimablePayout > 0n && !item.alreadyClaimed && (
                        <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-emerald-600">
                          Claimable: {formatPol(item.claimablePayout)} POL
                        </span>
                      )}
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link
                        to={`/market/${item.marketId.toString()}`}
                        className="rounded-xl border px-4 py-2 text-sm font-medium hover:bg-muted/50"
                      >
                        Open market
                      </Link>

                      {!item.resolved && item.yesShares > 0n && (
                        <button
                          type="button"
                          onClick={() => openSellDraft(item.marketId, "YES")}
                          className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-500/15"
                        >
                          Sell YES
                        </button>
                      )}

                      {!item.resolved && item.noShares > 0n && (
                        <button
                          type="button"
                          onClick={() => openSellDraft(item.marketId, "NO")}
                          className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-500/15"
                        >
                          Sell NO
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 lg:min-w-[360px]">
                    <div className="rounded-xl border bg-background p-4">
                      <div className="text-xs text-muted-foreground">
                        Current combined value
                      </div>
                      <div className="mt-1 text-xl font-semibold">
                        {formatPol(item.currentPositionValue)} POL
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        Total PnL: {formatSignedPol(item.pnlTotal)} POL
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleClaim(item.marketId)}
                      disabled={!canClaim}
                      className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {claimingMarketId === item.marketId.toString() &&
                      (isWritePending || isTxConfirming)
                        ? "Claiming..."
                        : item.alreadyClaimed
                        ? "Already Claimed"
                        : item.resolved
                        ? "Claim Winnings"
                        : "Claim After Resolution"}
                    </button>
                  </div>
                </div>

                {item.resolved && (
                  <div className="mt-5 grid gap-3 md:grid-cols-3">
                    <div className="rounded-xl border bg-background p-4">
                      <div className="text-xs text-muted-foreground">Resolved at</div>
                      <div className="mt-1 text-sm font-medium">
                        {formatDate(item.resolvedAt)}
                      </div>
                    </div>

                    <div className="rounded-xl border bg-background p-4">
                      <div className="text-xs text-muted-foreground">Resolved by</div>
                      <div className="mt-1 text-sm font-medium">
                        {shortAddress(item.resolver)}
                      </div>
                    </div>

                    <div className="rounded-xl border bg-background p-4">
                      <div className="text-xs text-muted-foreground">Evidence</div>
                      <div className="mt-1 text-sm font-medium">
                        {item.resolutionEvidenceUrl ? (
                          <a
                            href={item.resolutionEvidenceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                          >
                            Open evidence
                          </a>
                        ) : (
                          "—"
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {item.resolved && item.resolutionNotes && (
                  <div className="mt-3 rounded-xl border bg-background p-4">
                    <div className="text-xs text-muted-foreground">Resolution notes</div>
                    <div className="mt-1 text-sm font-medium whitespace-pre-wrap">
                      {item.resolutionNotes}
                    </div>
                  </div>
                )}

                {isSellingThisMarket && (
                  <div className="mt-5 rounded-2xl border border-border/70 bg-muted/20 p-5">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">
                          Sell {sellDraft?.side} shares
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          Available:{" "}
                          {sellDraft?.side === "YES"
                            ? formatPol(item.yesShares)
                            : formatPol(item.noShares)}{" "}
                          shares
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={closeSellDraft}
                        className="text-sm text-muted-foreground underline"
                      >
                        Cancel
                      </button>
                    </div>

                    <div className="mb-3">
                      <label className="mb-2 block text-sm font-medium">
                        Shares to sell
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={sellDraft?.amount ?? ""}
                          onChange={(e) => setSellAmount(e.target.value)}
                          placeholder="Enter shares amount"
                          className="h-12 w-full rounded-2xl border border-border/70 bg-background pl-4 pr-20 text-base outline-none transition focus:ring-2"
                        />
                        <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                          Shares
                        </div>
                      </div>

                      {sellTooHigh ? (
                        <p className="mt-2 text-sm text-rose-600">
                          You cannot sell more shares than you own.
                        </p>
                      ) : null}
                    </div>

                    <div className="mb-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSellAmount("0.1")}
                        className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
                      >
                        0.1
                      </button>
                      <button
                        type="button"
                        onClick={() => setSellAmount("0.25")}
                        className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
                      >
                        0.25
                      </button>
                      <button
                        type="button"
                        onClick={() => setSellAmount("0.5")}
                        className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
                      >
                        0.5
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setSellAmount(
                            formatEther(
                              sellDraft?.side === "YES"
                                ? item.yesShares
                                : item.noShares
                            )
                          )
                        }
                        className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
                      >
                        Max
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        handleSell(
                          item.marketId,
                          (sellDraft?.side ?? "YES") as "YES" | "NO"
                        )
                      }
                      disabled={!canSell}
                      className={`rounded-xl px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                        sellDraft?.side === "YES"
                          ? "bg-emerald-500 hover:bg-emerald-600"
                          : "bg-rose-500 hover:bg-rose-600"
                      }`}
                    >
                      {isWritePending || isTxConfirming
                        ? "Selling..."
                        : `Confirm Sell ${sellDraft?.side}`}
                    </button>
                  </div>
                )}

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border bg-background p-4">
                    <div className="mb-3 text-sm font-medium">YES Position</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Shares</span>
                        <span className="font-medium">{formatPol(item.yesShares)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Cost</span>
                        <span className="font-medium">{formatPol(item.yesCost)} POL</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Avg entry</span>
                        <span className="font-medium">
                          {formatPercentFromBps(item.avgYesEntryBps)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Current price</span>
                        <span className="font-medium">
                          {formatPercentFromBps(item.yesPriceBps)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Current value</span>
                        <span className="font-medium">
                          {formatPol(item.currentYesValue)} POL
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">PnL</span>
                        <span className="font-medium">
                          {formatSignedPol(item.pnlYes)} POL
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">If YES wins</span>
                        <span className="font-medium">
                          {formatPol(item.estimatedIfYesWins)} POL
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border bg-background p-4">
                    <div className="mb-3 text-sm font-medium">NO Position</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Shares</span>
                        <span className="font-medium">{formatPol(item.noShares)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Cost</span>
                        <span className="font-medium">{formatPol(item.noCost)} POL</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Avg entry</span>
                        <span className="font-medium">
                          {formatPercentFromBps(item.avgNoEntryBps)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Current price</span>
                        <span className="font-medium">
                          {formatPercentFromBps(item.noPriceBps)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Current value</span>
                        <span className="font-medium">
                          {formatPol(item.currentNoValue)} POL
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">PnL</span>
                        <span className="font-medium">
                          {formatSignedPol(item.pnlNo)} POL
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">If NO wins</span>
                        <span className="font-medium">
                          {formatPol(item.estimatedIfNoWins)} POL
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-5">
                  <div className="rounded-xl border bg-background p-4">
                    <div className="text-xs text-muted-foreground">YES Pool</div>
                    <div className="mt-1 text-lg font-semibold">
                      {formatPol(item.yesPool)} POL
                    </div>
                  </div>

                  <div className="rounded-xl border bg-background p-4">
                    <div className="text-xs text-muted-foreground">NO Pool</div>
                    <div className="mt-1 text-lg font-semibold">
                      {formatPol(item.noPool)} POL
                    </div>
                  </div>

                  <div className="rounded-xl border bg-background p-4">
                    <div className="text-xs text-muted-foreground">Total Pool</div>
                    <div className="mt-1 text-lg font-semibold">
                      {formatPol(totalPool)} POL
                    </div>
                  </div>

                  <div className="rounded-xl border bg-background p-4">
                    <div className="text-xs text-muted-foreground">YES Price</div>
                    <div className="mt-1 text-lg font-semibold">
                      {formatPercentFromBps(item.yesPriceBps)}
                    </div>
                  </div>

                  <div className="rounded-xl border bg-background p-4">
                    <div className="text-xs text-muted-foreground">NO Price</div>
                    <div className="mt-1 text-lg font-semibold">
                      {formatPercentFromBps(item.noPriceBps)}
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border bg-background p-4">
                    <div className="text-xs text-muted-foreground">Resolution source</div>
                    <div className="mt-1 text-sm font-medium">
                      {item.resolutionSource || "—"}
                    </div>
                  </div>

                  <div className="rounded-xl border bg-background p-4">
                    <div className="text-xs text-muted-foreground">Virtual liquidity</div>
                    <div className="mt-1 text-sm font-medium">
                      {formatPol(item.virtualLiquidity)} POL
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
